import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { binomialPmf } from '../prob/distributions';
import { binomialNormalCorrected } from '../prob/continuity-correction';
import { normalPdf, regionProbability, type RegionSpec } from '../prob/normal';
import { createPdfCurve, type PdfCurveHandle } from '../render/pdf-curve';
import { createSvgEl } from '@learnforge/demo-render';
import type { ProbContext, ProbKey } from '../types';
import { createSliderRow, createToggleRow } from '@learnforge/demo-kit';
import { paramFormat } from '../ui/param-format';

// ── Model ──────────────────────────────────────────────────────────────────

type SubMode = 'density' | 'bin-approx';

interface State {
  mode: SubMode;
  mu: number;
  sigma: number;
  regionType: RegionSpec['kind'];
  a: number;
  b: number;
  showZ: boolean;
  show68: boolean;
  showContinuity: boolean;
  n: number;
  p: number;
}

// ── Exported pure helper (tested) ─────────────────────────────────────────

export function regionFromInputs(type: RegionSpec['kind'], a: number, b: number): RegionSpec {
  switch (type) {
    case 'between':
      return { kind: 'between', a, b };
    case 'leq':
      return { kind: 'leq', a };
    case 'geq':
      return { kind: 'geq', a };
    case 'tails':
      return { kind: 'tails', a };
  }
}

// ── Spec factory ──────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, ProbKey, ProbContext> {
  return {
    labelKey: 'experiment.normal-calculator.label',
    demoId: 'probability',
    advancedKey: 'normal-calculator',

    initialState(): State {
      return {
        mode: 'density',
        mu: 0,
        sigma: 1,
        regionType: 'between',
        a: -1,
        b: 1,
        showZ: false,
        show68: false,
        showContinuity: true,
        n: 20,
        p: 0.5,
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage (built once) ──
      const stageSvg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'pb-nc-svg' });
      host.appendChild(stageSvg);
      const stageGroup = createSvgEl('g');
      stageSvg.appendChild(stageGroup);

      // Transient pdf-curve handle — destroyed + recreated on every state update.
      let curve: PdfCurveHandle | null = null;

      // Track the mode that was used for the most recent panel build so we know
      // when to rebuild the mode-conditional panel section.
      let panelMode: SubMode | null = null;

      // DOM refs for inputs that are synced by update() without rebuilding.
      // These are replaced when the panel is rebuilt (mode switch).
      let modeSel: HTMLSelectElement | null = null;
      let regSel: HTMLSelectElement | null = null;

      // ── renderStage: destroy previous curve, recreate from state ──
      function renderStage(state: State): void {
        curve?.destroy();
        stageGroup.replaceChildren();

        const { mode, mu, sigma, regionType, a, b, showZ, show68, showContinuity, n, p } = state;

        if (mode === 'density') {
          curve = createPdfCurve(stageGroup, {
            fn: (x) => normalPdf(x, mu, sigma),
            xDomain: [mu - 4 * sigma, mu + 4 * sigma],
            shadedRegion: regionFromInputs(regionType, a, b),
            showSigmaBands: show68,
            showZScoreTicks: showZ,
            mu,
            sigma,
            xLabelKey: 'axis.value',
            yLabelKey: 'axis.density',
            i18n: ctx.i18n,
          });
          const probTxt = createSvgEl('text', {
            class: 'pb-nc-prob',
            x: 400,
            y: 30,
            'text-anchor': 'middle',
          });
          probTxt.textContent = `P = ${regionProbability(regionFromInputs(regionType, a, b), mu, sigma).toFixed(4)}`;
          stageGroup.appendChild(probTxt);
        } else {
          // bin-approx
          const moments = { mean: n * p, variance: n * p * (1 - p) };
          const binSigma = Math.sqrt(moments.variance);
          const shadedA = showContinuity ? a - 0.5 : a;
          const shadedB = showContinuity ? b + 0.5 : b;
          curve = createPdfCurve(stageGroup, {
            fn: (x) => normalPdf(x, moments.mean, binSigma),
            xDomain: [moments.mean - 4 * binSigma, moments.mean + 4 * binSigma],
            shadedRegion: { kind: 'between', a: shadedA, b: shadedB },
            showSigmaBands: show68,
            showZScoreTicks: showZ,
            mu: moments.mean,
            sigma: binSigma,
            xLabelKey: 'axis.count-bin',
            yLabelKey: 'axis.density',
            i18n: ctx.i18n,
          });
          // Bars overlay the pdf curve in the same coordinate system the chart helpers use.
          // Mirrors the PAD_L/PAD_R/PAD_T/PAD_B constants in render/pdf-curve.ts.
          const VIEW_W = 800;
          const VIEW_H = 400;
          const PAD_L = 56;
          const PAD_R = 24;
          const PAD_T = 28;
          const PAD_B = 56;
          const xMin = moments.mean - 4 * binSigma;
          const xMax = moments.mean + 4 * binSigma;
          const peakDensity = normalPdf(moments.mean, moments.mean, binSigma) * 1.1 || 1;
          const toX = (x: number) =>
            PAD_L + ((x - xMin) / (xMax - xMin)) * (VIEW_W - PAD_L - PAD_R);
          const toY = (y: number) => VIEW_H - PAD_B - (y / peakDensity) * (VIEW_H - PAD_T - PAD_B);
          const barWidth = Math.max(2, (toX(xMin + 1) - toX(xMin)) * 0.85);
          for (let k = 0; k <= n; k++) {
            const pmf = binomialPmf(n, p, k);
            const bar = createSvgEl('rect', {
              class: 'pb-nc-binom-bar',
              x: toX(k) - barWidth / 2,
              y: toY(pmf),
              width: barWidth,
              height: Math.max(0, VIEW_H - PAD_B - toY(pmf)),
            });
            stageGroup.appendChild(bar);
          }
          const probTxt = createSvgEl('text', {
            class: 'pb-nc-prob',
            x: 400,
            y: 30,
            'text-anchor': 'middle',
          });
          if (showContinuity) {
            const cc = binomialNormalCorrected(n, p, a, b);
            probTxt.textContent = `P(${a}..${b}) ≈ ${cc.toFixed(4)} (continuity-corrected)`;
          } else {
            const raw = regionProbability({ kind: 'between', a, b }, moments.mean, binSigma);
            probTxt.textContent = `P(${a}..${b}) ≈ ${raw.toFixed(4)}`;
          }
          stageGroup.appendChild(probTxt);
        }
      }

      // ── renderPanel: build (or rebuild) bespoke panel DOM ──
      // Called once on first render, and again only when mode changes.
      function renderPanel(state: State): void {
        ctx.panel.replaceChildren();
        modeSel = null;
        regSel = null;

        const { mode, mu, sigma, regionType, a, b, showZ, show68, showContinuity, n, p } = state;

        // Mode selector
        const modeLbl = document.createElement('label');
        modeLbl.textContent = ctx.i18n.t('param.submode.label');
        const sel = document.createElement('select');
        for (const m of ['density', 'bin-approx'] as SubMode[]) {
          const o = document.createElement('option');
          o.value = m;
          o.textContent = ctx.i18n.t(`submode.normal-calculator.${m}.label` as never);
          sel.appendChild(o);
        }
        sel.value = mode;
        sel.addEventListener('change', () => {
          controls.setState((s) => ({ ...s, mode: sel.value as SubMode }));
        });
        const modeRow = document.createElement('div');
        modeRow.className = 'pb-dropdown-row';
        modeRow.append(modeLbl, sel);
        ctx.panel.appendChild(modeRow);
        modeSel = sel;

        // Helper — adds a param slider bound to controls.setState
        function addSlider(
          labelKey: ProbKey,
          min: number,
          max: number,
          step: number,
          value: number,
          setter: (s: State, v: number) => State,
          format?: (v: number) => string,
        ): void {
          const row = createSliderRow({
            id: `nc-${labelKey}`,
            label: ctx.i18n.t(labelKey),
            i18n: ctx.i18n,
            min,
            max,
            step,
            value,
            format: format ?? paramFormat,
            onChange: (v) => {
              controls.setState((s) => setter(s, v));
            },
          });
          ctx.panel.appendChild(row.element);
        }

        if (mode === 'density') {
          const oneDecimal = (v: number): string => v.toFixed(1);
          addSlider('param.mu.label', -5, 5, 0.1, mu, (s, v) => ({ ...s, mu: v }), oneDecimal);
          addSlider(
            'param.sigma.label',
            0.1,
            5,
            0.1,
            sigma,
            (s, v) => ({ ...s, sigma: v }),
            oneDecimal,
          );

          const regLbl = document.createElement('label');
          regLbl.textContent = ctx.i18n.t('param.region-type.label');
          const rSel = document.createElement('select');
          for (const k of ['between', 'leq', 'geq', 'tails'] as const) {
            const o = document.createElement('option');
            o.value = k;
            o.textContent = ctx.i18n.t(`region.${k}.label` as never);
            rSel.appendChild(o);
          }
          rSel.value = regionType;
          rSel.addEventListener('change', () => {
            controls.setState((s) => ({ ...s, regionType: rSel.value as RegionSpec['kind'] }));
          });
          const regionRow = document.createElement('div');
          regionRow.className = 'pb-dropdown-row';
          regionRow.append(regLbl, rSel);
          ctx.panel.appendChild(regionRow);
          regSel = rSel;

          addSlider('param.a-bound.label', -5, 5, 0.1, a, (s, v) => ({ ...s, a: v }), oneDecimal);
          if (regionType === 'between') {
            addSlider('param.b-bound.label', -5, 5, 0.1, b, (s, v) => ({ ...s, b: v }), oneDecimal);
          }
        } else {
          addSlider('param.n.label', 1, 200, 1, n, (s, v) => ({ ...s, n: v }));
          addSlider(
            'param.p.label',
            0.01,
            0.99,
            0.01,
            p,
            (s, v) => ({ ...s, p: v }),
            (v) => v.toFixed(2),
          );
          addSlider('param.a-bound.label', 0, 200, 1, a, (s, v) => ({ ...s, a: v }));
          addSlider('param.b-bound.label', 0, 200, 1, b, (s, v) => ({ ...s, b: v }));
        }

        // Toggles (checkboxes)
        const toggles: Array<readonly [ProbKey, boolean, (s: State, v: boolean) => State]> = [
          ['param.show-z-scores.label', showZ, (s, v) => ({ ...s, showZ: v })],
          ['param.show-68-95-99.label', show68, (s, v) => ({ ...s, show68: v })],
        ];
        if (mode === 'bin-approx') {
          toggles.push([
            'param.show-continuity-correction.label',
            showContinuity,
            (s, v) => ({ ...s, showContinuity: v }),
          ]);
        }
        for (const [labelKey, initial, setter] of toggles) {
          const toggle = createToggleRow({
            labelKey,
            i18n: ctx.i18n,
            checked: initial,
            onChange: (checked) => {
              controls.setState((s) => setter(s, checked));
            },
          });
          toggle.input.id = `nc-${labelKey}`;
          ctx.panel.appendChild(toggle.element);
        }

        panelMode = mode;
      }

      // ── RenderHandle ──────────────────────────────────────────────────────

      function update(state: State): void {
        // Rebuild the panel only when mode changes (mode-conditional section differs).
        if (state.mode !== panelMode) {
          renderPanel(state);
        } else {
          // Guard slider/select sync: only update when the element is not focused
          // to avoid clobbering in-progress user edits.
          if (modeSel && document.activeElement !== modeSel) {
            modeSel.value = state.mode;
          }
          if (regSel && document.activeElement !== regSel) {
            regSel.value = state.regionType;
          }
          // Note: individual param sliders (createSliderRow) use 'input' events
          // and their value is authoritative while focused; we do not sync them
          // back here to avoid fighting the user. The panel rebuild on mode switch
          // recreates them with the current state values, which is sufficient.
        }
        renderStage(state);
      }

      return {
        update,
        dispose() {
          // Narrow dispose: only tear down the pdf-curve handle.
          // The harness owns replaceChildren on ctx.root / ctx.panel / ctx.actionBar.
          curve?.destroy();
          curve = null;
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Registry alias — keeps index.ts unchanged.
export const mountNormalCalculator = experiment.mount.bind(experiment);
